import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LngLatBounds } from 'mapbox-gl'
import { MapboxEvent, MapRef } from 'react-map-gl'
import useConstants from '@core/providers/useConstants'
import { useProjectMapConfiguration } from '@core/react-query/features/projects/maps'
import { MapBoxMap, MapProps as BaseMapProps } from '@common/material/MapBox'
import {
  createMapboxLayer,
  GEOJSON_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP,
} from '@modules/geolocation'
import {
  createDXFMapboxLayer,
  DXF_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP,
} from '@modules/geolocation/utilities/createDXFMapboxLayer'
import { registerHatchImageHandler } from '@modules/geolocation/utilities/hatchPattern'
import { useToast } from '@core/toast'
import { MapDisclaimer } from './components/MapDisclaimer'
import { MapLayersList } from './components/MapLayersList'
import { ProjectMapLayer } from '@core/entities/project/ProjectMapLayer'
import { Box, Flex } from '@fundamentals'
import { CircularProgress, IconButton, Tooltip } from '@common/material'
import { ControlCameraOutlined } from '@mui/icons-material'
import { localizedStrings } from '@core/strings'

/**
 * Map component loads map configuration and images for project-specific mapping functionality.
 *
 * Props:
 * - `projectId`: The ID of the project to load specific map configurations.
 * - `serverConfig` (object, optional):
 *   Flags to determine which parts of the configuration should be fetched from the server.
 *   All flags default to `true`.
 *   - `initialViewState` (boolean): Fetch the map's initial center and zoom from the server.
 *   - `mapStyle` (boolean): Fetch the map's style URL from the server.
 *   - `zoomConfiguration` (boolean): Fetch zoom enablement settings from the server.
 *   - `maxBounds` (boolean): Fetch the map's bounding box constraints from the server.
 * - `mapRef`: Reference to the Mapbox map instance.
 * - `children`: Optional children to be rendered within the map.
 *
 * Extends:
 * - `BaseMapProps` for additional MapBoxMap props.
 */
type MapProps = {
  projectId: number
  serverConfig?: {
    initialViewState?: boolean
    mapStyle?: boolean
    zoomConfiguration?: boolean
    maxBounds?: boolean
    layers?: boolean
    disclaimer?: boolean
    disclaimerActions?: boolean
  }
  mapRef: MutableRefObject<MapRef>
  children?: React.ReactNode
  disclaimer?: string
  disclaimerActions?: { url: string; label: string }[]
  isMapLayersListInProjectMapContext?: boolean
  hideDisclaimer?: boolean
  hideMapLayersList?: boolean
  hideResetCameraToProjectBoundariesButton?: boolean
  onLayersLoaded?: () => void
} & BaseMapProps

const Map: React.FC<MapProps> = ({
  projectId,
  serverConfig: serverConfigOverride,
  onLayersLoaded,
  onLoad,
  mapRef: externalMapRef,
  children,
  mapStyle: mapStyleOverride,
  initialViewState: initialViewStateOverride,
  maxBounds: maxBoundsOverride,
  scrollZoom: scrollZoomOverride,
  boxZoom: boxZoomOverride,
  doubleClickZoom: doubleClickZoomOverride,
  disclaimer: disclaimerOverride,
  disclaimerActions: disclaimerActionsOverride,
  isMapLayersListInProjectMapContext = false,
  hideDisclaimer = false,
  hideMapLayersList = false,
  hideResetCameraToProjectBoundariesButton = false,
  ...props
}) => {
  const [projectMapLayers, setProjectMapLayers] = useState<ProjectMapLayer[]>(
    [],
  )

  // If we pass in a mapRef, we have direct access to the mapbox-gl map object for full flexibility
  const internalMapRef = useRef<MapRef>(null)
  const mapRef = externalMapRef || internalMapRef
  const { constants } = useConstants()
  const toast = useToast()

  const serverConfig = useMemo(() => {
    return {
      initialViewState: serverConfigOverride?.initialViewState ?? true,
      mapStyle: serverConfigOverride?.mapStyle ?? true,
      zoomConfiguration: serverConfigOverride?.zoomConfiguration ?? true,
      maxBounds: serverConfigOverride?.maxBounds ?? true,
      layers: serverConfigOverride?.layers ?? true,
      disclaimer: serverConfigOverride?.disclaimer ?? true,
      disclaimerActions: serverConfigOverride?.disclaimerActions ?? true,
    }
  }, [serverConfigOverride])

  // Fetch the project-specific map configuration using projectId
  const {
    data: projectMapConfigurationFromServer,
    isLoading: isProjectMapConfigurationLoading,
  } = useProjectMapConfiguration(
    {
      projectId,
    },
    { enabled: Object.values(serverConfig).some((config) => config) }, // Only fetch if any of the configurations are enabled,
  )

  // Determine the initial view state of the map.
  // Priority order:
  // 1. If `serverConfig.initialViewState` is true and server data is available,
  //    use the project's map configuration from the server (center and zoom).
  // 2. If `initialViewStateOverride` is provided, use the override values.
  // 3. Fallback to default values defined in constants.
  const initialViewState = useMemo(() => {
    if (
      serverConfig.initialViewState &&
      projectMapConfigurationFromServer?.mapCenterLongitude &&
      projectMapConfigurationFromServer?.mapCenterLatitude &&
      projectMapConfigurationFromServer?.mapZoom
    ) {
      return {
        latitude: projectMapConfigurationFromServer.mapCenterLatitude,
        longitude: projectMapConfigurationFromServer.mapCenterLongitude,
        zoom: projectMapConfigurationFromServer.mapZoom,
      }
    }

    if (initialViewStateOverride) {
      return initialViewStateOverride
    }

    return {
      latitude: constants.mapbox_default_center_latitude,
      longitude: constants.mapbox_default_center_longitude,
      zoom: constants.mapbox_default_zoom,
    }
  }, [projectMapConfigurationFromServer])

  // Determine the Mapbox style URL for the map.
  // Priority order:
  // 1. If `serverConfig.mapStyle` is true and server data is available,
  //    use the map style URL from the server.
  // 2. If `mapStyleOverride` is provided, use the override value.
  // 3. Fallback to the default style URL defined in constants.
  const mapBoxStyleUrl = useMemo(() => {
    if (
      serverConfig.mapStyle &&
      projectMapConfigurationFromServer?.mapBoxStyleUrl
    ) {
      return projectMapConfigurationFromServer.mapBoxStyleUrl
    }

    if (mapStyleOverride) {
      return mapStyleOverride
    }

    return constants.mapbox_default_style_url
  }, [projectMapConfigurationFromServer])

  // Determine the zooming behavior for the map.
  // Priority order:
  // 1. If `serverConfig.zoomConfiguration` is true and server data is available:
  //    - Use the server-provided `canZoom` configuration to enable or disable zooming features.
  //      - If `canZoom` is true, enable `scrollZoom`, `boxZoom`, and `doubleClickZoom`.
  //      - If `canZoom` is false, disable all zooming features.
  // 2. If `serverConfig.zoomConfiguration` is false:
  //    - Use the override values provided as `scrollZoomOverride`, `boxZoomOverride`, and `doubleClickZoomOverride`.
  // 3. Fallback behavior:
  //    - Enable all zooming features (`true`) if no overrides are provided.
  const zoomConfiguration = useMemo(() => {
    if (serverConfig.zoomConfiguration) {
      if (projectMapConfigurationFromServer?.canZoom) {
        return {
          scrollZoom: true,
          boxZoom: true,
          doubleClickZoom: true,
        }
      }

      return {
        scrollZoom: false,
        boxZoom: false,
        doubleClickZoom: false,
      }
    }

    return {
      scrollZoom: scrollZoomOverride ?? true,
      boxZoom: boxZoomOverride ?? true,
      doubleClickZoom: doubleClickZoomOverride ?? true,
    }
  }, [projectMapConfigurationFromServer?.canZoom])

  // Determine the maximum bounds for the map.
  // Priority order:
  // 1. If `serverConfig.maxBounds` is true and server data is available:
  //    - Use the bounding box constraints (`maxBounds`) from the server configuration.
  //    - Create a new `LngLatBounds` object using the southwest and northeast coordinates from the server.
  // 2. If `serverConfig.maxBounds` is false:
  //    - Use the override value provided as `maxBoundsOverride`.
  // 3. Fallback behavior:
  //    - If neither server configuration nor overrides are available, return `null` to indicate no bounds.
  const maxBoundsConfiguration = useMemo(() => {
    if (
      serverConfig.maxBounds &&
      projectMapConfigurationFromServer?.maxBounds
    ) {
      return new LngLatBounds([
        [
          projectMapConfigurationFromServer.maxBounds.southwest.longitude,
          projectMapConfigurationFromServer.maxBounds.southwest.latitude,
        ],
        [
          projectMapConfigurationFromServer.maxBounds.northeast.longitude,
          projectMapConfigurationFromServer.maxBounds.northeast.latitude,
        ],
      ])
    }

    if (maxBoundsOverride) {
      return maxBoundsOverride
    }

    return null
  }, [projectMapConfigurationFromServer?.maxBounds])

  useEffect(() => {
    const serverLayers =
      projectMapConfigurationFromServer?.layers?.toReversed() ?? []

    // HACK ALERT: the configurator sets `layers: false` because it renders
    // KMZ/KML itself, but it can't render tileset layers — so those ignore
    // the flag and always draw here. Proper fix: split the flag instead of
    // special-casing by layer kind.
    const layersToRender = serverConfig.layers
      ? serverLayers
      : serverLayers.filter((layer) => !!layer.tilesetId)

    setProjectMapLayers(layersToRender)
  }, [projectMapConfigurationFromServer?.layers, serverConfig.layers])

  // Permit/checklist layer IDs that must always sit on top of project layers
  // (DXF tilesets, GeoJSON files). Listed in render order — the first is the
  // bottom of the on-top stack.
  const aboveProjectLayerIds = useMemo(
    () => [
      // Location boundary polygons (ProjectMap) sit at the bottom of the
      // entity stack: above DXF/GeoJSON project layers, below permit zones
      // and markers.
      'location-boundaries-fill-layer',
      'location-boundaries-line-layer',
      'location-boundaries-symbol-layer',
      // GeoPolygon (e.g. the read-only TimelineMap card) renders permit areas
      // under the `polygon` id — keep them above DXF/GeoJSON project layers.
      'polygon-fill-layer',
      'polygon-line-layer',
      'permit-zones-fill-layer',
      'permit-zones-line-layer',
      'permit-markers-symbol-layer-unselected',
      'permit-markers-symbol-layer-selected',
      'permit-markers-symbol-layer-current',
      'checklist-markers-symbol-layer-unselected',
      'checklist-markers-symbol-layer-selected',
      'checklist-markers-symbol-layer-current',
    ],
    [],
  )

  // Style-layer suffixes a single project layer can produce, listed
  // bottom-to-top — derived from the layer factories so the lists can't
  // drift apart. Used to find a project layer's lowest style layer so we can
  // anchor beneath it, and (reversed) to remove a layer's style layers.
  const styleSuffixesBottomToTop = useMemo(
    () =>
      Array.from(
        new Set([
          ...DXF_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP,
          ...GEOJSON_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP,
        ]),
      ),
    [],
  )

  // The bottom-most style layer currently on the map for a given project layer.
  const getBottomStyleLayerId = useCallback(
    (layerId: number | string): string | undefined => {
      const map = mapRef.current?.getMap()
      if (!map) return undefined
      const sourceId = layerId.toString()
      return styleSuffixesBottomToTop
        .map((suffix) => `${sourceId}${suffix}`)
        .find((id) => map.getLayer(id))
    },
    [mapRef, styleSuffixesBottomToTop],
  )

  // Determine the beforeId a (re)added project layer should be anchored to so
  // the configured z-order is preserved when toggling layers on and off.
  // `projectMapLayers` is ordered bottom-to-top (index 0 is the lowest layer),
  // so the layer must sit below the first layer above it that is currently on
  // the map. If none are present, anchor below the permit/checklist layers so
  // DXF/GeoJSON content stays beneath them.
  const getBeforeIdForProjectLayer = useCallback(
    (targetLayer?: ProjectMapLayer): string | undefined => {
      const map = mapRef.current?.getMap()
      if (!map) return undefined

      if (targetLayer) {
        const targetIndex = projectMapLayers.findIndex(
          (l) => l.id === targetLayer.id,
        )
        if (targetIndex !== -1) {
          for (let i = targetIndex + 1; i < projectMapLayers.length; i++) {
            const aboveStyleId = getBottomStyleLayerId(projectMapLayers[i].id)
            if (aboveStyleId) return aboveStyleId
          }
        }
      }

      return aboveProjectLayerIds.find((id) => map.getLayer(id))
    },
    [mapRef, aboveProjectLayerIds, projectMapLayers, getBottomStyleLayerId],
  )

  // Defensive fallback for the reverse race: if a project layer was added to
  // the top before any permit/checklist layer existed, lift the permit layers
  // back above by moving each existing one to the top (in render order).
  const movePermitLayersAboveProjectLayers = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    aboveProjectLayerIds.forEach((id) => {
      if (map.getLayer(id)) map.moveLayer(id)
    })
    // Drawn permit areas (mapbox-gl-draw) must sit on top of everything,
    // including DXF/GeoJSON project layers and permit zones/markers. Their
    // layer ids are generated by mapbox-gl-draw (each style id gets a
    // `.cold`/`.hot` suffix), so we match by the `gl-draw-` prefix and lift
    // each to the top in its existing order — toggling a project layer can
    // then never cover the area the user is drawing.
    map
      .getStyle()
      .layers.filter((l) => l.id.startsWith('gl-draw-'))
      .forEach((l) => map.moveLayer(l.id))
  }, [mapRef, aboveProjectLayerIds])

  // Helper to add a project's layer to the Mapbox map. Returns false when the
  // layer can't be rendered (missing config, unsupported renderer) so callers
  // can surface the failure instead of flipping a toggle with nothing shown.
  const addLayerToMap = useCallback(
    (layer: ProjectMapLayer): boolean => {
      const map = mapRef.current?.getMap()

      if (!map) return false

      const sourceId = layer.id.toString()

      if (layer.tilesetId) {
        // Only the DXF renderer is supported for tileset layers, and the
        // config must carry the tileset's vector layer name — resolved when
        // the tileset is published, never fetched at runtime.
        if (layer.renderer !== 'DXF' || !layer.tilesetSourceLayer) {
          return false
        }

        // Add vector source if missing
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`,
          })
        }

        const mapboxLayers = createDXFMapboxLayer({
          id: sourceId,
          sourceLayer: layer.tilesetSourceLayer,
          // Centre latitude of the tileset — used to size DXF text at true
          // ground scale (Web-Mercator's metres-per-pixel depends on latitude).
          centerLat:
            layer.tilesetCenterLat ?? constants.mapbox_default_center_latitude,
        })
        mapboxLayers.forEach((styleLayer) => {
          if (!map.getLayer(styleLayer.id)) {
            map.addLayer(styleLayer, getBeforeIdForProjectLayer(layer))
          }
        })
        movePermitLayersAboveProjectLayers()
        return true
      }

      if (!layer.geoJsonDownloadUrl) {
        return false
      }

      // Add geojson source if missing
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'geojson',
          data: layer.geoJsonDownloadUrl,
        })
      }

      const mapboxLayers = createMapboxLayer({ id: sourceId })
      mapboxLayers.forEach((styleLayer) => {
        if (!map.getLayer(styleLayer.id)) {
          map.addLayer(styleLayer, getBeforeIdForProjectLayer(layer))
        }
      })
      movePermitLayersAboveProjectLayers()
      return true
    },
    [
      mapRef,
      getBeforeIdForProjectLayer,
      movePermitLayersAboveProjectLayers,
      constants.mapbox_default_center_latitude,
    ],
  )

  // Helper to remove a project's layer from the Mapbox map
  const removeLayerFromMap = useCallback(
    (layerId: number | string) => {
      const map = mapRef.current?.getMap()

      if (!map) return

      const sourceId = layerId.toString()

      // Always need to remove the style layers (top-down) before the source
      const suffixesTopToBottom = [...styleSuffixesBottomToTop].reverse()
      suffixesTopToBottom.forEach((suffix) => {
        const id = `${sourceId}${suffix}`
        if (map.getLayer(id)) {
          map.removeLayer(id)
        }
      })

      // Finally remove the source
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    },
    [mapRef, styleSuffixesBottomToTop],
  )

  // Toggle a layer visibility
  const toggleLayerVisibility = useCallback(
    (layerId: number | string) => {
      const targetLayer = projectMapLayers.find((l) => l.id === layerId)
      if (!targetLayer) return
      const updatedVisible = !targetLayer.visible

      // Apply to map immediately
      if (updatedVisible) {
        const added = addLayerToMap(targetLayer)
        if (!added) {
          // Leave the toggle off and say so, rather than flipping it on with
          // nothing rendered.
          toast.error(
            localizedStrings.mapLayerCouldNotBeDisplayed(targetLayer.name),
          )
          return
        }
      } else {
        removeLayerFromMap(layerId)
      }

      // Update state
      setProjectMapLayers((currentLayers) =>
        currentLayers.map((layer) => {
          if (layer.id === layerId) {
            return ProjectMapLayer.new({ ...layer, visible: updatedVisible })
          }
          return layer
        }),
      )
    },
    [addLayerToMap, removeLayerFromMap, projectMapLayers, toast],
  )

  // Toggle all layers visibility
  const toggleAllLayersVisibility = useCallback(
    (visible: boolean) => {
      // Apply to map first; layers that fail to add keep their previous
      // visibility so the list reflects what is actually rendered.
      const failedLayerIds = new Set<number | string>()
      projectMapLayers.forEach((layer) => {
        if (visible && !layer.visible) {
          const added = addLayerToMap(layer)
          if (!added) {
            failedLayerIds.add(layer.id)
            toast.error(
              localizedStrings.mapLayerCouldNotBeDisplayed(layer.name),
            )
          }
        }
        if (!visible && layer.visible) {
          removeLayerFromMap(layer.id)
        }
      })

      // Then update state
      setProjectMapLayers((currentLayers) =>
        currentLayers.map((layer) => {
          if (failedLayerIds.has(layer.id)) {
            return layer
          }
          return ProjectMapLayer.new({ ...layer, visible })
        }),
      )
    },
    [addLayerToMap, removeLayerFromMap, projectMapLayers, toast],
  )

  // Add every visible project layer to the map. The layer list state already
  // respects `serverConfig.layers` (it stays empty when layers are disabled).
  const loadLayers = useCallback(() => {
    const visibleLayers = projectMapLayers?.filter((l) => l.visible) ?? []
    visibleLayers.forEach((layer) => {
      const added = addLayerToMap(layer)
      if (!added) {
        toast.error(localizedStrings.mapLayerCouldNotBeDisplayed(layer.name))
      }
    })
  }, [projectMapLayers, addLayerToMap, toast])

  // Handles map load event to load images needed for markers
  const _onLoad = async (e: MapboxEvent) => {
    // Generate per-colour diagonal-hatch images on demand so DXF pattern
    // (non-solid) hatches render with a hatch fill rather than nothing.
    const map = mapRef.current?.getMap()
    if (map) registerHatchImageHandler(map)

    // Define images to be used as markers in the map
    const imagesToLoad = [
      {
        url: '/images/icons/markers/permit_marker_white.png',
        name: 'unselected-marker-icon',
      },
      {
        url: '/images/icons/markers/permit_marker_purple.png',
        name: 'selected-marker-icon',
      },
      {
        url: '/images/icons/markers/permit_marker_red.png',
        name: 'current-marker-icon',
      },
    ]

    // Load each image and add it to the map's symbol layers if not already present
    imagesToLoad.forEach(({ url, name }) => {
      mapRef.current?.loadImage(url, (error, image) => {
        if (error) return

        // Add the image only if it hasn't been added before
        if (!mapRef.current.hasImage(name)) mapRef.current.addImage(name, image)
      })
    })

    // Add project layers (DXF tilesets, GeoJSON) FIRST so that anything the
    // caller adds in its own onLoad (draw controls, permit/checklist layers,
    // etc.) stacks on top of the project layers.
    loadLayers()
    onLayersLoaded?.()

    // Trigger the onLoad callback, if provided
    onLoad?.(e)
  }

  // Determine the disclaimer text to display on the map.
  // Priority order:
  // 1. If `serverConfig.disclaimer` is true and server data is available,
  //    use the disclaimer text from the server.
  // 2. If `disclaimerOverride` is provided, use the override value.
  // 3. Fallback to null if no disclaimer is available.
  const disclaimerConfiguration = useMemo(() => {
    if (
      serverConfig.disclaimer &&
      projectMapConfigurationFromServer?.disclaimer
    ) {
      return projectMapConfigurationFromServer.disclaimer
    }

    if (disclaimerOverride) {
      return disclaimerOverride
    }

    return null
  }, [
    projectMapConfigurationFromServer?.disclaimer,
    disclaimerOverride,
    serverConfig.disclaimer,
  ])

  // Determine the disclaimer actions to display on the map.
  // Priority order:
  // 1. If `serverConfig.disclaimerActions` is true and server data is available,
  //    use the disclaimer actions from the server.
  // 2. If `disclaimerActionsOverride` is provided, use the override value.
  // 3. Fallback to null if no disclaimer actions are available.
  const getDisclaimerActionsConfiguration = () => {
    if (
      serverConfig.disclaimerActions &&
      projectMapConfigurationFromServer?.disclaimerActions
    ) {
      return projectMapConfigurationFromServer.disclaimerActions
    }

    if (disclaimerActionsOverride) {
      return disclaimerActionsOverride
    }

    return null
  }

  const disclaimerActionsConfiguration = getDisclaimerActionsConfiguration()

  // Reset camera to initial view state
  const resetCameraToInitialView = useCallback(() => {
    if (!mapRef.current) return
    const { latitude, longitude, zoom } = initialViewState
    if (
      latitude === undefined ||
      longitude === undefined ||
      zoom === undefined
    ) {
      return
    }

    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom,
      essential: true,
    })
  }, [initialViewState, mapRef])

  // Calculate position for the floating overlay stack (layers button + reset button, disclaimer below)
  const floatingControlsPosition = useMemo(() => {
    const top = isMapLayersListInProjectMapContext ? 75 : 12
    const right = 12
    return { top, right }
  }, [isMapLayersListInProjectMapContext])

  if (isProjectMapConfigurationLoading) {
    return (
      <Flex
        justifyContent='center'
        alignItems='center'
        style={{ width: '100%', height: '100%' }}
      >
        <CircularProgress />
      </Flex>
    )
  }

  return (
    <MapBoxMap
      mapRef={mapRef}
      onLoad={_onLoad}
      mapStyle={mapBoxStyleUrl}
      initialViewState={initialViewState}
      scrollZoom={zoomConfiguration.scrollZoom}
      boxZoom={zoomConfiguration.boxZoom}
      doubleClickZoom={zoomConfiguration.doubleClickZoom}
      maxBounds={maxBoundsConfiguration}
      {...props}
    >
      <Box
        sx={{
          position: 'absolute',
          top: floatingControlsPosition.top,
          right: floatingControlsPosition.right,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
          zIndex: 1,
        }}
      >
        {((serverConfig.layers && !hideMapLayersList) ||
          !hideResetCameraToProjectBoundariesButton) && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {serverConfig.layers && !hideMapLayersList && (
              <MapLayersList
                projectMapLayers={projectMapLayers}
                onToggleLayerVisibility={toggleLayerVisibility}
                onToggleAllLayersVisibility={toggleAllLayersVisibility}
              />
            )}
            {!hideResetCameraToProjectBoundariesButton && (
              <Tooltip title={localizedStrings.resetToProjectBoundaries}>
                <IconButton
                  onClick={resetCameraToInitialView}
                  sx={{
                    backgroundColor: 'white',
                    color: 'text.secondary',
                    boxShadow: 1,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'grey.200',
                    },
                  }}
                >
                  <ControlCameraOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        {(disclaimerConfiguration ||
          disclaimerActionsConfiguration?.length > 0) &&
          !hideDisclaimer && (
            <MapDisclaimer
              text={disclaimerConfiguration}
              actions={disclaimerActionsConfiguration}
            />
          )}
      </Box>

      {children}
    </MapBoxMap>
  )
}

export { Map }
